import { useMemo } from 'react';

import { graphql } from '@/__generated__';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useQueryStable } from '../useQueryStable';

export const GetRelatedProjects = graphql(/* GraphQL */ `
  query RelatedProjects($environmentId: BigInt!) {
    environment(id: $environmentId) {
      consumerProjects {
        consumerCloudProject
        consumerCoreProject
        consumerEnvironmentId
        consumerProjectId
        producerPublicModels {
          uniqueId
          numEdges
        }
      }
      producerProjects {
        producerCoreProject
        producerEnvironmentId
        producerProjectId
        producerPublicModels {
          uniqueId
          numEdges
        }
      }
    }
  }
`);

type RelatedProjectProps = {
  environmentId?: number;
  pause?: boolean;
};

const useRelatedProjectsInternal = ({
  environmentId = 0,
  pause,
}: RelatedProjectProps) => {
  const [query] = useQueryStable({
    query: GetRelatedProjects,
    variables: { environmentId: environmentId },
    pause: pause || !environmentId,
  });
  return query;
};

type ProducerProject = NonNullable<
  ReturnType<typeof useRelatedProjectsInternal>['data']
>;

type PublicModelEdgeResponse =
  ProducerProject['environment']['producerProjects'][number]['producerPublicModels'][number];

export type ProducerProjectsData = NonNullable<
  ReturnType<typeof useProducerProjects>['data']
>;

export type PublicModelEdge = PublicModelEdgeResponse & {
  environmentId: number | null;
  projectId: number | null;
};

export const useProducerProjects = (props: RelatedProjectProps) => {
  const query = useRelatedProjectsInternal(props);

  return useMemo(() => {
    const references: PublicModelEdge[] = [];

    if (query.data) {
      query.data.environment.producerProjects.forEach((project) => {
        project.producerPublicModels.forEach((model) => {
          if (model.numEdges) {
            references.push({
              ...model,
              environmentId: project.producerEnvironmentId,
              projectId: project.producerProjectId,
            });
          }
        });
      });
    }
    return {
      ...query,
      data: query.data ? { ...query.data.environment, references } : null,
    };
  }, [query]);
};

export const useConsumerProjects = (props: RelatedProjectProps) => {
  const query = useRelatedProjectsInternal(props);
  const producerProjectId = useDbtCloudProject().projectId;

  return useMemo(() => {
    const producerEnvironmentId = props.environmentId ?? null;
    const references: PublicModelEdge[] = [];

    if (query.data) {
      query.data.environment.consumerProjects.forEach((project) => {
        project.producerPublicModels.forEach((model) => {
          if (model.numEdges) {
            references.push({
              ...model,
              environmentId: producerEnvironmentId,
              projectId: producerProjectId,
            });
          }
        });
      });
    }

    return {
      ...query,
      data: query.data
        ? { ...query.data.environment, producerEnvironmentId, references }
        : null,
    };
  }, [producerProjectId, props.environmentId, query]);
};
